<template>
  <article class="event-documents">
    <h3 class="event-documents__header">
      {{ $t("event.documents.title") }}

      <icon
        name="event-documents-header"
        class="event-documents__header-icon"
      />
    </h3>

    <Notification
      :warning="false"
      icon="warning-border--no-color"
      modify="_flex _documents"
    >
      <span v-html="$t('event.documents.notification')"></span>
    </Notification>

    <div class="event-documents__content">
      <table class="event-documents__table">
        <tbody>
          <tr>
            <td>{{ $t("event.documents.issuedInvoices") }}</td>
            <td>
              <span>{{ contractsAndInvoicesCount }}</span>
            </td>
            <td>
              <a
                class="event-documents__content-look"
                href="#"
                @click.prevent="
                  $modal.show('event-modal-documents-issued-invoices')
                "
              >
                <Tooltip :label="$t('common.eventCardLook')">
                  <icon name="look" />
                </Tooltip>
              </a>
            </td>
          </tr>

          <tr>
            <td>{{ $t("event.documents.acts") }}</td>
            <td>
              <span>{{ actsCount }}</span>
            </td>
            <td>
              <a
                class="event-documents__content-look"
                href="#"
                @click.prevent="$modal.show('event-modal-documents-acts')"
              >
                <tooltip :label="$t('common.eventCardLook')">
                  <icon name="look" />
                </tooltip>
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="event-documents__content__footer">
      <div class="event-documents__content__footer-text">
        <icon name="info-grey" />
        <p style="margin: 0">
          {{ $t("event.documents.downloadForm1") }}
          <a
            class="link"
            :href="
              exhibition.website + getLocaleValue(exhibition, 'car_pass_terms')
            "
            target="_blank"
          >
            {{ $t("event.documents.downloadForm2") }}
          </a>
        </p>
      </div>
    </div>
  </article>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { i18n } from "@v2/shared/config";
import { getLocaleValue } from "@/helpers/common";
import { Tooltip, Notification } from "@v2/shared/ui/components";

export default {
  name: "EventDocuments",

  components: {
    Notification,
    Tooltip
  },
  props: {
    eventId: {
      type: Number,
      required: true
    }
  },
  computed: {
    ...mapState("documents", ["contracts"]),
    ...mapGetters("event", ["isEventMontage", "exhibition"]),

    contractsAndInvoicesCount() {
      return this.contracts.length + this.invoicesCount;
    },
    locale() {
      return i18n.locale;
    },
    invoicesCount() {
      return this.contracts.length
        ? this.contracts.reduce((count, contract) => {
            return (
              count +
              contract.documents.filter(document => document.type === "invoice")
                .length
            );
          }, 0)
        : 0;
    },

    actsCount() {
      return this.contracts.length
        ? this.contracts.reduce((count, contract) => {
            return (
              count +
              contract.documents.filter(document => document.type === "act")
                .length
            );
          }, 0)
        : 0;
    }
  },

  methods: {
    getLocaleValue,
    async initContracts() {
      await this.$store.dispatch("documents/getContracts", this.eventId);
    }
  },

  mounted() {
    if (!this.isEventMontage) {
      this.initContracts();
    }
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-documents {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 460px;
  margin-top: 20px;
  box-sizing: border-box;

  @include respond-down(lg) {
    width: 350px;
  }
  @include respond-down(md) {
    margin-top: 15px;
  }

  &__header {
    @include h2();
    position: relative;
    padding: 20px 30px;

    @include respond-down(md) {
      padding: 15px;
      font-size: 1rem;
    }

    border-bottom: 1px solid var(--gray-darker);

    &-icon {
      position: absolute;
      right: 19px;
      bottom: 0;
      width: 58px;
      height: 65px;

      @include respond-down(lg) {
        width: 56px;
        height: 53px;
      }
    }
  }

  &__content {
    padding: 30px;
    &__footer {
      padding: 23px 0 24px 40px;
      border-top: 1px solid rgba(205, 206, 208, 0.5);
      @include respond-down(lg) {
        padding: 23px 0 24px 30px;
      }
      &-text {
        font-size: 13px;
        font-weight: normal;
        font-style: normal;
        font-stretch: normal;
        line-height: 1.54;
        letter-spacing: normal;
        color: #6d7179;
        margin: 0;
        padding: 0;
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        align-items: center;
        & .svg-icon {
          width: 16px;
          height: 16px;
          margin-right: 8px;
        }
        & .link {
          @include hyperlink();
        }
      }
    }
    &-look {
      display: flex;
    }

    @include respond-down(md) {
      padding: 15px;
    }
  }

  &__table {
    width: 100%;
    font-size: 13px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: 20px;
    letter-spacing: normal;
    color: #000000;

    @include respond-down(md) {
      font-size: 12px;
      line-height: 1.67;
    }

    td {
      padding: 15px;
    }

    tr:not(:last-child) td {
      border-bottom: 1px solid rgba(205, 206, 208, 0.5);
    }

    td:nth-child(1) {
      width: 240px;
      padding-right: 14px;

      @include respond-down(lg) {
        width: 130px;
      }
    }

    td:nth-child(2) {
      width: 70px;
      padding-right: 15px;

      @include respond-down(lg) {
        width: 60px;
      }

      color: #6d7179;
    }

    td:nth-child(3) {
      width: 44px;
      text-align: right;
    }

    .svg-icon {
      width: 20px;
      height: 20px;
      display: block;
      color: #009ee3;
    }
  }
}
</style>

<style lang="scss">
@import "@v2/app/styles/media";

.event-documents {
  .notification {
    &__text {
      padding-right: 20px;
      line-height: 18px;

      @include respond-down(lg) {
        padding-right: 0;
      }
    }
  }
}
</style>
